import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Timer, Info } from 'lucide-react';
import { toast } from 'sonner';

export default function AutoCierreCard() {
  const [habilitado, setHabilitado] = useState(true);
  const [horas, setHoras] = useState(12);
  const [opciones, setOpciones] = useState(Array.from({ length: 16 }, (_, i) => i + 1));
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/config/auto-cierre');
        setHabilitado(!!data.auto_cierre_habilitado);
        setHoras(Number(data.auto_cierre_horas) || 12);
        if (Array.isArray(data.opciones_horas) && data.opciones_horas.length > 0) {
          setOpciones(data.opciones_horas);
        }
      } catch {
        toast.error('No se pudo cargar la configuración de auto-cierre');
      } finally { setCargando(false); }
    })();
  }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put('/admin/config/auto-cierre', {
        auto_cierre_habilitado: habilitado,
        auto_cierre_horas: Number(horas),
      });
      toast.success(habilitado ? `Auto-cierre activado (a partir de ${horas} horas)` : 'Auto-cierre desactivado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally { setGuardando(false); }
  };

  return (
    <Card className="border-zinc-200" data-testid="auto-cierre-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2 text-lg">
          <Timer className="h-5 w-5" /> Auto-cierre de jornadas olvidadas
        </CardTitle>
        <CardDescription>
          Si un empleado se olvida de fichar la salida, el sistema cierra su jornada de forma automática.
          El empleado deberá confirmar o rechazar el cierre desde la app o el kiosko.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {cargando ? (
          <p className="text-sm text-zinc-400 text-center py-4">Cargando configuración...</p>
        ) : (
          <>
            <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-200 bg-zinc-50/50">
              <div className="space-y-0.5">
                <Label htmlFor="ac-habilitado" className="text-sm font-semibold">Activar auto-cierre</Label>
                <p className="text-xs text-zinc-500">Recomendado. Sin esto, las jornadas quedarían abiertas indefinidamente.</p>
              </div>
              <Switch id="ac-habilitado" checked={habilitado} onCheckedChange={setHabilitado} data-testid="auto-cierre-switch" />
            </div>

            <div className={`space-y-2 ${!habilitado ? 'opacity-40 pointer-events-none' : ''}`}>
              <Label className="text-sm font-semibold">Umbral de cierre</Label>
              <p className="text-xs text-zinc-500">
                Horas transcurridas desde la entrada tras las que se asume que el empleado ha olvidado fichar la salida.
              </p>
              <Select value={String(horas)} onValueChange={(v) => setHoras(Number(v))} disabled={!habilitado}>
                <SelectTrigger className="max-w-xs" data-testid="auto-cierre-horas"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {opciones.map((h) => (
                    <SelectItem key={h} value={String(h)}>{h} horas</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                Elige un valor <strong>mayor que la jornada más larga</strong> de tu empresa (turnos, guardias, etc.)
                para no cerrar jornadas todavía en curso. 12 horas suele ser suficiente en la mayoría de negocios.
              </p>
            </div>

            <Button onClick={guardar} disabled={guardando} className="bg-zinc-900 hover:bg-zinc-800" data-testid="auto-cierre-guardar">
              {guardando ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              {guardando ? 'Guardando...' : 'Guardar configuración'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
